import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash, Plus, Minus, ShoppingBag, WhatsappLogo, ArrowLeft, Tag, X, Percent } from '@phosphor-icons/react';
import { useCartStore } from '../store';
import { formatPrice, generateWhatsAppURL } from '../utils/helpers';
import { getActiveCoupons, validateCoupon } from '../utils/api';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const Cart = () => {
  const { items, removeItem, updateQuantity, clearCart, getSubtotal } = useCartStore();
  const [couponInput, setCouponInput] = useState('');
  const [showCoupons, setShowCoupons] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  
  const subtotal = getSubtotal();
  const total = subtotal - discountAmount;

  useEffect(() => {
    fetchCoupons();
  }, []);

  // Recalculate discount when subtotal changes
  useEffect(() => {
    if (appliedCoupon) {
      recalculateDiscount(appliedCoupon);
    }
  }, [subtotal]);

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await getActiveCoupons();
      if (response.data.success) {
        setAvailableCoupons(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const recalculateDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      let discount = (subtotal * coupon.discount_value) / 100;
      if (coupon.max_discount_amount) {
        discount = Math.min(discount, coupon.max_discount_amount);
      }
      setDiscountAmount(discount);
    } else {
      setDiscountAmount(coupon.discount_value);
    }
  };

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    const code = couponInput.toUpperCase().trim();
    
    if (!code) return;
    
    try {
      const response = await validateCoupon(code, subtotal);
      if (response.data.valid) {
        const couponData = response.data.data;
        setAppliedCoupon(couponData);
        setDiscountAmount(couponData.discount_amount);
        toast.success(`Coupon applied! ${couponData.discount_type === 'percentage' ? couponData.discount_value + '%' : '₹' + couponData.discount_value} off`);
        setCouponInput('');
        setShowCoupons(false);
      } else {
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      toast.error('Invalid coupon code');
    }
  };

  const handleSelectCoupon = async (coupon) => {
    // Check minimum order amount
    if (coupon.min_order_amount && subtotal < coupon.min_order_amount) {
      toast.error(`Minimum order amount is ₹${coupon.min_order_amount}`);
      return;
    }
    
    try {
      const response = await validateCoupon(coupon.code, subtotal);
      if (response.data.valid) {
        const couponData = response.data.data;
        setAppliedCoupon(couponData);
        setDiscountAmount(couponData.discount_amount);
        toast.success(`Coupon applied! ${couponData.discount_type === 'percentage' ? couponData.discount_value + '%' : '₹' + couponData.discount_value} off`);
        setShowCoupons(false);
      } else {
        toast.error(response.data.message || 'Cannot apply this coupon');
      }
    } catch (error) {
      toast.error('Cannot apply this coupon');
    }
  };

  const removeDiscount = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
  };

  const handleWhatsAppCheckout = () => {
    let message = generateWhatsAppURL(items, total);
    if (appliedCoupon) {
      message = message.replace('Total:', `Discount (${appliedCoupon.code}): -${formatPrice(discountAmount)}\nTotal:`);
    }
    window.open(message, '_blank');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background-default flex items-center justify-center">
        <SEO title="Shopping Cart" description="Your Luster Adorn shopping cart" url="/cart" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-24 h-24 bg-background-subtle rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag size={48} className="text-text-muted" weight="light" />
          </div>
          <h2 className="font-display text-3xl text-secondary mb-4">Your cart is empty</h2>
          <p className="font-body text-text-secondary mb-8">
            Looks like you haven't added anything yet
          </p>
          <Link to="/shop" data-testid="continue-shopping-btn" className="btn-primary">
            Continue Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-default">
      <SEO title="Shopping Cart" description="Review your items and checkout via WhatsApp" url="/cart" />
      
      {/* Header */}
      <section className="bg-background-subtle py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl text-secondary text-center">Shopping Cart</h1>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 font-body text-sm text-text-secondary hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Continue Shopping
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {/* Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 py-4 border-b border-border-default font-body text-sm text-text-secondary uppercase tracking-wider">
                <div className="col-span-6">Product</div>
                <div className="col-span-2 text-center">Price</div>
                <div className="col-span-2 text-center">Quantity</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              {/* Items */}
              {items.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  data-testid={`cart-item-${item.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 py-6 border-b border-border-default items-center"
                >
                  {/* Product Info */}
                  <div className="md:col-span-6 flex gap-4">
                    <Link to={`/product/${item.id}`} className="w-24 h-24 flex-shrink-0 overflow-hidden">
                      <img
                        src={item.image_urls?.[0] || 'https://images.unsplash.com/photo-1736436789706-005f2218a96d?w=200'}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                    <div className="flex flex-col justify-center">
                      <Link 
                        to={`/product/${item.id}`} 
                        className="font-display text-lg text-secondary hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                      {item.category_name && (
                        <span className="font-body text-sm text-text-muted">{item.category_name}</span>
                      )}
                      <button
                        onClick={() => removeItem(item.id)}
                        data-testid={`remove-item-${item.id}`}
                        className="inline-flex items-center gap-1 font-body text-sm text-red-500 hover:text-red-600 transition-colors mt-2 md:hidden"
                      >
                        <Trash size={14} />
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="md:col-span-2 text-center">
                    <span className="md:hidden font-body text-sm text-text-secondary">Price: </span>
                    <span className="font-body text-secondary">{formatPrice(item.selling_rate)}</span>
                  </div>

                  {/* Quantity */}
                  <div className="md:col-span-2 flex items-center justify-center">
                    <div className="flex items-center border border-border-default">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        data-testid={`decrease-${item.id}`}
                        className="p-2 hover:bg-background-subtle transition-colors"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-4 font-body text-sm">{item.quantity}</span>
                      <button
                        onClick={() => {
                          const availableStock = item.available_stock || 0;
                          if (availableStock > 0 && item.quantity >= availableStock) {
                            toast.error(`Only ${availableStock} items available in stock`);
                            return;
                          }
                          updateQuantity(item.id, item.quantity + 1);
                        }}
                        data-testid={`increase-${item.id}`}
                        className="p-2 hover:bg-background-subtle transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="md:col-span-2 flex items-center justify-between md:justify-end">
                    <span className="md:hidden font-body text-sm text-text-secondary">Total: </span>
                    <div className="flex items-center gap-4">
                      <span className="font-display text-lg text-secondary">
                        {formatPrice(item.selling_rate * item.quantity)}
                      </span>
                      <button
                        onClick={() => removeItem(item.id)}
                        data-testid={`remove-${item.id}`}
                        className="hidden md:block text-text-muted hover:text-red-500 transition-colors"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Clear Cart */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={clearCart}
                  data-testid="clear-cart-btn"
                  className="font-body text-sm text-text-secondary hover:text-red-500 transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 border border-border-default sticky top-28">
                <h3 className="font-display text-xl text-secondary mb-6">Order Summary</h3>
                
                {/* Coupon Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-body text-sm text-text-secondary">Have a coupon?</span>
                    {/* <button
                      onClick={() => setShowCoupons(!showCoupons)}
                      className="font-body text-xs text-primary hover:underline"
                      data-testid="view-coupons-btn"
                    >
                      {loadingCoupons ? 'Loading...' : 'View available'}
                    </button> */}
                  </div>
                  
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center gap-2">
                        <Tag size={16} className="text-green-600" />
                        <span className="font-body text-sm text-green-700">{appliedCoupon.code}</span>
                      </div>
                      <button onClick={removeDiscount} className="text-green-600 hover:text-green-800">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleApplyCoupon} className="flex gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        placeholder="Enter code"
                        data-testid="coupon-input"
                        className="flex-1 px-3 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
                      />
                      <button type="submit" data-testid="apply-coupon-btn" className="px-4 py-2 bg-secondary text-white font-body text-sm hover:bg-secondary-hover transition-colors">
                        Apply
                      </button>
                    </form>
                  )}

                  {/* Available Coupons Dropdown */}
                  {showCoupons && !appliedCoupon && availableCoupons.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 border border-border-default rounded overflow-hidden"
                    >
                      {availableCoupons.map((coupon) => (
                        <button
                          key={coupon.code}
                          onClick={() => handleSelectCoupon(coupon)}
                          data-testid={`coupon-${coupon.code}`}
                          className="w-full p-3 text-left hover:bg-background-subtle transition-colors border-b border-border-default last:border-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-body text-sm font-medium text-secondary">{coupon.code}</span>
                            <span className="font-body text-sm text-primary">
                              {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `₹${coupon.discount_value} OFF`}
                            </span>
                          </div>
                          <p className="font-body text-xs text-text-muted mt-1">{coupon.description}</p>
                          {coupon.min_order_amount > 0 && (
                            <p className="font-body text-xs text-orange-500 mt-1">
                              Min. order: ₹{coupon.min_order_amount}
                            </p>
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-secondary">Subtotal ({items.length} items)</span>
                    <span className="text-secondary">{formatPrice(subtotal)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-green-600 flex items-center gap-1">
                        <Percent size={14} /> Discount
                      </span>
                      <span className="text-green-600">-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-secondary">Shipping</span>
                    <span className="text-text-secondary">Calculated at checkout</span>
                  </div>
                </div>

                <div className="border-t border-border-default pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="font-display text-lg text-secondary">Total</span>
                    <span className="font-display text-xl text-primary" data-testid="cart-total">
                      {formatPrice(total)}
                    </span>
                  </div>
                  {discountAmount > 0 && (
                    <p className="font-body text-xs text-green-600 text-right mt-1">
                      You save {formatPrice(discountAmount)}!
                    </p>
                  )}
                </div>

                {/* WhatsApp Checkout */}
                <button
                  onClick={handleWhatsAppCheckout}
                  data-testid="whatsapp-checkout-btn"
                  className="whatsapp-btn w-full justify-center mb-4"
                >
                  <WhatsappLogo size={22} weight="fill" />
                  <span className="font-body font-medium">Checkout on WhatsApp</span>
                </button>

                <p className="font-body text-xs text-text-muted text-center">
                  Click to send your order via WhatsApp. We'll confirm availability and share payment details.
                </p>

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-border-default grid grid-cols-2 gap-2">
                  <div className="text-center p-2">
                    <p className="font-body text-xs text-text-muted">Secure</p>
                    <p className="font-body text-xs text-secondary">Payment</p>
                  </div>
                  <div className="text-center p-2">
                    <p className="font-body text-xs text-text-muted">Easy</p>
                    <p className="font-body text-xs text-secondary">Returns</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
